import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, X } from "lucide-react";
import api from "../api";
import Logo from "../components/Logo";
import Seo from "../components/Seo";

const inputCls =
  "w-full bg-obsidian-3 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:border-ember focus:outline-none transition-colors";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[9px] tracking-[0.2em] uppercase text-slate-400">{label}</span>
      <input {...props} className={inputCls} />
    </label>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-obsidian px-4">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-obsidian-2 p-8">
        <Logo />
        <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-ember">Admin CMS</p>
        <div className="mt-6 space-y-4">
          <Field label="Email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field label="Password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={busy}
          className="mt-6 w-full bg-ember py-3.5 font-mono text-[11px] tracking-[0.25em] text-[#10141B] transition-colors hover:bg-ember-hover disabled:opacity-60"
        >
          {busy ? "SIGNING IN…" : "SIGN IN"}
        </button>
      </form>
    </div>
  );
}

const EMPTY_PRODUCT = {
  name: "", brand: "BKT", segment: "manglam-enterprises", category: "",
  applications: "", equipment: "", size: "", construction: "Radial",
  tyre_type: "CVR", pattern: "", position: "", description: "", image: "", sample: false,
};
const EMPTY_NEWS = { title: "", category: "Company Updates", excerpt: "", body: "", image: "" };
const EMPTY_CASE = { title: "", customer: "", industry: "", equipment: "", application: "", challenge: "", solution: "", result: "", placeholder: true };

function toLists(doc) {
  return { ...doc, applications: (doc.applications || []).join(", "), equipment: (doc.equipment || []).join(", "), sizes: (doc.sizes || []).join(", ") };
}
function fromLists(form) {
  const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  return { ...form, applications: split(form.applications), equipment: split(form.equipment), sizes: split(form.sizes || "") };
}

function CrudTab({ endpoint, empty, fields, rowTitle, testid, transformIn, transformOut }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, [endpoint]);

  const openNew = () => { setEditId(null); setForm({ ...empty }); };
  const openEdit = (item) => { setEditId(item.id); setForm(transformIn ? transformIn(item) : { ...empty, ...item }); };

  const save = async (e) => {
    e.preventDefault();
    const payload = transformOut ? transformOut(form) : form;
    try {
      if (editId) await api.put(`/admin/${endpoint}/${editId}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      toast.success("Saved.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      toast.success("Deleted.");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`admin-tab-${testid}`}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">{items.length} entries</p>
        <button onClick={openNew} data-testid={`admin-add-${testid}`} className="inline-flex items-center gap-2 bg-ember px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] text-[#10141B] hover:bg-ember-hover">
          <Plus className="h-3.5 w-3.5" /> ADD NEW
        </button>
      </div>

      {form && (
        <form onSubmit={save} className="mt-5 border border-white/10 bg-obsidian-3 p-5" data-testid={`admin-form-${testid}`}>
          <div className="flex items-center justify-between mb-4">
            <p className="font-condensed text-lg font-bold uppercase text-white">{editId ? "Edit entry" : "New entry"}</p>
            <button type="button" onClick={() => setForm(null)} data-testid={`admin-form-close-${testid}`} aria-label="Close form"><X className="h-4 w-4 text-slate-400" /></button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map((f) =>
              f.type === "textarea" ? (
                <label key={f.key} className="block sm:col-span-2 lg:col-span-3">
                  <span className="mb-1.5 block font-mono text-[9px] tracking-[0.2em] uppercase text-slate-400">{f.label}</span>
                  <textarea rows={4} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={`${inputCls} resize-none`} data-testid={`admin-field-${f.key}`} />
                </label>
              ) : f.type === "select" ? (
                <label key={f.key} className="block">
                  <span className="mb-1.5 block font-mono text-[9px] tracking-[0.2em] uppercase text-slate-400">{f.label}</span>
                  <select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} data-testid={`admin-field-${f.key}`}>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              ) : f.type === "checkbox" ? (
                <label key={f.key} className="flex items-center gap-2 pt-6">
                  <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} data-testid={`admin-field-${f.key}`} />
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-slate-400">{f.label}</span>
                </label>
              ) : (
                <Field key={f.key} label={f.label} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`admin-field-${f.key}`} />
              )
            )}
          </div>
          <button type="submit" data-testid={`admin-save-${testid}`} className="mt-5 bg-ember px-6 py-3 font-mono text-[10px] tracking-[0.22em] text-[#10141B] hover:bg-ember-hover">
            SAVE
          </button>
        </form>
      )}

      <div className="mt-5 divide-y divide-white/5 border border-white/10">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-4 bg-obsidian-2 px-4 py-3.5" data-testid={`admin-row-${item.id}`}>
            <div className="min-w-0">
              <p className="truncate font-condensed text-lg font-semibold uppercase text-white">{rowTitle(item)}</p>
              <p className="truncate font-mono text-[9px] tracking-[0.15em] uppercase text-slate-400">
                {[item.size, item.tyre_type, item.category, item.industry, item.segment].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => openEdit(item)} data-testid={`admin-edit-${item.id}`} aria-label="Edit" className="border border-white/15 p-2 text-slate-300 hover:border-ember hover:text-ember"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(item.id)} data-testid={`admin-delete-${item.id}`} aria-label="Delete" className="border border-white/15 p-2 text-slate-300 hover:border-red-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="bg-obsidian-2 px-4 py-8 text-center text-sm text-slate-400">No entries yet.</p>}
      </div>
    </div>
  );
}

const PRODUCT_FIELDS = [
  { key: "name", label: "Product name" },
  { key: "brand", label: "Brand" },
  { key: "segment", label: "Segment", type: "select", options: ["manglam-enterprises", "apex-infra", "amr"] },
  { key: "category", label: "Category" },
  { key: "size", label: "Tyre size" },
  { key: "construction", label: "Construction", type: "select", options: ["Radial", "Bias"] },
  { key: "tyre_type", label: "Tyre type", type: "select", options: ["CVR", "OTR", "Industrial", "Radial", "Bias"] },
  { key: "pattern", label: "Pattern" },
  { key: "sizes", label: "Sizes (comma separated)" },
  { key: "position", label: "Position" },
  { key: "applications", label: "Applications (comma separated)" },
  { key: "equipment", label: "Equipment (comma separated)" },
  { key: "image", label: "Image URL" },
  { key: "sample", label: "Mark as sample", type: "checkbox" },
  { key: "description", label: "Description", type: "textarea" },
];

const NEWS_FIELDS = [
  { key: "title", label: "Title" },
  { key: "category", label: "Category", type: "select", options: ["Mining", "Tyres", "Fleet Management", "Technology", "Industry News", "Company Updates"] },
  { key: "image", label: "Image URL" },
  { key: "excerpt", label: "Excerpt", type: "textarea" },
  { key: "body", label: "Body", type: "textarea" },
];

const CASE_FIELDS = [
  { key: "title", label: "Title" },
  { key: "customer", label: "Customer / Project" },
  { key: "industry", label: "Industry" },
  { key: "equipment", label: "Equipment" },
  { key: "application", label: "Tyre Application" },
  { key: "placeholder", label: "Mark as placeholder", type: "checkbox" },
  { key: "challenge", label: "Challenge", type: "textarea" },
  { key: "solution", label: "Solution", type: "textarea" },
  { key: "result", label: "Result", type: "textarea" },
];

function EnquiriesTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/enquiries").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-tab-enquiries" className="divide-y divide-white/5 border border-white/10">
      {items.map((e) => (
        <div key={e.id} className="bg-obsidian-2 px-4 py-4" data-testid={`admin-enquiry-${e.id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-condensed text-lg font-semibold uppercase text-white">{e.name}{e.company ? ` — ${e.company}` : ""}</p>
            <p className="font-mono text-[9px] tracking-[0.15em] text-slate-400">{e.created_at ? new Date(e.created_at).toLocaleString("en-IN") : ""}</p>
          </div>
          <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-ember">{[e.phone, e.email, e.location].filter(Boolean).join(" · ")}</p>
          <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-slate-400">{[e.industry, e.equipment, e.tyre_size, e.requirement].filter(Boolean).join(" · ")}</p>
          {e.message && <p className="mt-2 text-sm text-slate-400">{e.message}</p>}
        </div>
      ))}
      {items.length === 0 && <p className="bg-obsidian-2 px-4 py-8 text-center text-sm text-slate-400">No enquiries yet.</p>}
    </div>
  );
}

function SimpleFormTab({ endpoint, fields, testid }) {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get(`/${endpoint}`).then((r) => setForm(r.data)).catch(() => setForm({})); }, [endpoint]);
  if (!form) return null;
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/${endpoint}`, form);
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };
  return (
    <form onSubmit={save} data-testid={`admin-tab-${testid}`} className="border border-white/10 bg-obsidian-2 p-5">
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((f) => (
          <Field key={f.key} label={f.label} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`admin-field-${f.key}`} />
        ))}
      </div>
      <button type="submit" data-testid={`admin-save-${testid}`} className="mt-5 bg-ember px-6 py-3 font-mono text-[10px] tracking-[0.22em] text-[#10141B] hover:bg-ember-hover">SAVE</button>
    </form>
  );
}

const TABS = [
  { id: "enquiries", label: "Enquiries" },
  { id: "products", label: "Products" },
  { id: "news", label: "News" },
  { id: "case-studies", label: "Case Studies" },
  { id: "stats", label: "Stats" },
  { id: "settings", label: "Contact Info" },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  if (user === null) return <div className="min-h-screen bg-obsidian" />;
  if (!user) return (<><Seo title="Admin — Mangalam Group" noindex /><Login onLogin={setUser} /></>);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-obsidian">
      <Seo title="Admin CMS — Mangalam Group" noindex />
      <header className="border-b border-white/10 bg-obsidian-2">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Logo />
          <div className="flex items-center gap-4">
            <p className="hidden sm:block font-mono text-[10px] tracking-[0.2em] text-slate-400">{user.email}</p>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-slate-300 hover:border-ember hover:text-ember">
              <LogOut className="h-3.5 w-3.5" /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-button-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
                tab === t.id ? "bg-ember text-[#10141B]" : "border border-white/15 text-slate-300 hover:border-ember hover:text-ember"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "enquiries" && <EnquiriesTab />}
          {tab === "products" && (
            <CrudTab endpoint="products" empty={EMPTY_PRODUCT} fields={PRODUCT_FIELDS} testid="products"
              rowTitle={(p) => `${p.brand} ${p.name}`} transformIn={toLists} transformOut={fromLists} />
          )}
          {tab === "news" && (
            <CrudTab endpoint="news" empty={EMPTY_NEWS} fields={NEWS_FIELDS} testid="news" rowTitle={(n) => n.title} />
          )}
          {tab === "case-studies" && (
            <CrudTab endpoint="case-studies" empty={EMPTY_CASE} fields={CASE_FIELDS} testid="case-studies" rowTitle={(c) => c.title} />
          )}
          {tab === "stats" && (
            <SimpleFormTab endpoint="stats" testid="stats" fields={[
              { key: "tyres_tracked", label: "Tyres tracked" },
              { key: "vehicles_managed", label: "Vehicles managed" },
              { key: "lifecycle", label: "Lifecycle note" },
              { key: "visibility", label: "Visibility note" },
            ]} />
          )}
          {tab === "settings" && (
            <SimpleFormTab endpoint="settings" testid="settings" fields={[
              { key: "phone", label: "Phone" },
              { key: "email", label: "Email" },
              { key: "address", label: "Address" },
              { key: "whatsapp", label: "WhatsApp link" },
            ]} />
          )}
        </div>
      </main>
    </div>
  );
}
